import { REGION } from '@env';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';

import { FsDataDogRumService } from '@lib/data-dog-rum';
import { LprIntegrationType } from '@lib/data-dog-rum/custom-action-map';

import { IFsLprFlockV3Parameters } from '../api/interfaces/lpr-flock-v3-parameters.interface';
import { FlockV3LprResults } from '../api/interfaces/lpr-flock-v3-results';
import { SnLprV3FlockRead } from '../api/models/lpr-flock-v3-read';
import {
  extractLprErrorCode,
  extractLprRawError,
  parseLprApiError,
} from '../utils/lpr-error-utils';

@Injectable({
  providedIn: 'root',
})
// TODO: FWX-1117 - Reevaluate custom action metrics vs using global trackResources
export class FlockV3LprService implements OnDestroy {
  private readonly _destroy$ = new Subject<void>();
  private readonly _integrationType: LprIntegrationType = 'flock_ps_v3';

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _metrics: FsDataDogRumService,
  ) {}

  get region(): string {
    return REGION;
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * @param params are the params required by the Flock API of the type {@link IFsLprFlockV3Parameters}
   * @returns Response from the API of type {@link FlockV3LprResults}
   */
  public getFlockPlatesV3(params: IFsLprFlockV3Parameters): Observable<FlockV3LprResults> {
    const startTime = Date.now();

    return this._httpClient
      .get<FlockV3LprResults>('/api/integrations/flock_v3/plate_search/', {
        params: params as any,
        headers: {
          'X-fs-skip-error': 'true',
        },
      })
      .pipe(
        map((response: FlockV3LprResults) => {
          if (Array.isArray(response.results)) {
            response.results = response.results.map((result) => new SnLprV3FlockRead(result));
          }
          this._metrics.addAction('lpr_search', {
            status: 200,
            duration_ms: Date.now() - startTime,
            integration: this._integrationType,
          });
          return response;
        }),
        catchError((error) => {
          this._metrics.addAction('lpr_search_error', {
            status: error?.status || 500,
            duration_ms: Date.now() - startTime,
            integration: this._integrationType,
            raw_error: extractLprRawError(error),
            error_code: extractLprErrorCode(error),
            error_message: parseLprApiError(error),
          });
          return throwError(error);
        }),
      );
  }

  /**
   * @param pageId is the pageId of the next page to fetch
   * @returns Response from the API of type {@link FlockV3LprResults}
   */
  public getFlockPlatesV3NextPage(pageId: string): Observable<FlockV3LprResults> {
    const startTime = Date.now();
    return this._httpClient
      .get<FlockV3LprResults>(`/api/integrations/flock/v3/reads/lookup/page/${pageId}/`, {
        headers: {
          'X-fs-skip-error': 'true',
        },
      })
      .pipe(
        map((response: FlockV3LprResults) => {
          if (Array.isArray(response.results)) {
            response.results = response.results.map((result) => new SnLprV3FlockRead(result));
          }
          this._metrics.addAction('lpr_search', {
            status: 200,
            duration_ms: Date.now() - startTime,
            integration: this._integrationType,
          });
          return response;
        }),
        catchError((error) => {
          this._metrics.addAction('lpr_search_error', {
            status: error?.status || 500,
            duration_ms: Date.now() - startTime,
            integration: this._integrationType,
            raw_error: extractLprRawError(error),
            error_code: extractLprErrorCode(error),
            error_message: parseLprApiError(error),
          });
          return throwError(error);
        }),
      );
  }

  public destroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
